import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { X } from 'lucide-react'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { useAppAuth } from '@/contexts/app-auth.context'
import { knowledgeApiClient } from '@/lib/api/knowledge.client'
import type { Listing, LeaseAgentMetadata } from '@super/shared-types'
import { OutboundCallType } from '@super/shared-types'
import {
  parsePhoneNumberWithError,
  isValidPhoneNumber,
} from 'libphonenumber-js'

const INSTRUCTIONS_STORAGE_KEY = 'simulate_lease_instructions'

const simulateLeaseSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().refine(val => {
    try {
      return isValidPhoneNumber(val, 'US')
    } catch {
      return false
    }
  }, 'Valid phone number is required'),
  message: z.string().optional(),
  instructions: z.string().optional(),
})

type SimulateLeaseFormValues = z.infer<typeof simulateLeaseSchema>

interface SimulateLeaseModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit?: (data: SimulateLeaseFormValues) => Promise<void>
}

function formatPhoneNumber(value: string): string {
  if (value.startsWith('+')) {
    return value.replace(/\s+/g, ' ').trim()
  }
  const numbers = value.replace(/\D/g, '')
  if (numbers.length > 10) return value
  if (numbers.length === 0) return ''
  if (numbers.length <= 3) return `(${numbers}`
  if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`
}

export function SimulateLeaseModal({
  open,
  onOpenChange,
  onSubmit: onSubmitProp,
}: SimulateLeaseModalProps) {
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [selectedListing, setSelectedListing] =
    useState<SearchDropdownItem<Listing> | null>(null)
  const [allListingItems, setAllListingItems] = useState<
    SearchDropdownItem<Listing>[]
  >([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [accordionValue, setAccordionValue] = useState<string>('')
  const [storedInstructions, setStoredInstructions] = useState<string>('')

  const form = useForm<SimulateLeaseFormValues>({
    resolver: zodResolver(simulateLeaseSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: "I'm interested in your listing...",
      instructions: '',
    },
  })

  useEffect(() => {
    const saved = localStorage.getItem(INSTRUCTIONS_STORAGE_KEY)
    if (saved) {
      setStoredInstructions(saved)
      form.setValue('instructions', saved)
    }
  }, [form])

  useEffect(() => {
    if (open) {
      const saved = localStorage.getItem(INSTRUCTIONS_STORAGE_KEY)
      form.reset({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: "I'm interested in your listing...",
        instructions: saved || '',
      })
      setSelectedListing(null)
      setStoredInstructions(saved || '')
      setAccordionValue('')

      const fetchAll =
        isSuperAdmin && currentCompany?.id
          ? knowledgeApiClient.getAdminListingsForCompany(currentCompany.id)
          : knowledgeApiClient.getListings()
      fetchAll.then((listings: Listing[]) =>
        setAllListingItems(
          listings.map((l: Listing) => ({
            id: l.id,
            label: l.address,
            sublabel: [
              l.unit ? `Unit ${l.unit}` : null,
              l.beds != null ? `${l.beds}bd` : null,
              l.baths != null ? `${l.baths}ba` : null,
              l.monthlyRentDollars ? `$${l.monthlyRentDollars}/mo` : null,
            ]
              .filter(Boolean)
              .join(' · '),
            data: l,
          })),
        ),
      )
    }
  }, [open, form, isSuperAdmin, currentCompany?.id])

  const handleInstructionsChange = (value: string) => {
    if (value.trim()) {
      localStorage.setItem(INSTRUCTIONS_STORAGE_KEY, value)
      setStoredInstructions(value)
    }
  }

  const handleClearInstructions = (e: React.MouseEvent) => {
    e.stopPropagation()
    localStorage.removeItem(INSTRUCTIONS_STORAGE_KEY)
    setStoredInstructions('')
    form.setValue('instructions', '')
    setAccordionValue('instructions')
  }

  const fetchListings = async (
    query?: string,
  ): Promise<SearchDropdownItem<Listing>[]> => {
    if (!query?.trim()) return allListingItems
    const q = query.toLowerCase()
    return allListingItems.filter(item => item.label.toLowerCase().includes(q))
  }

  const onSubmit: SubmitHandler<SimulateLeaseFormValues> = async data => {
    if (!currentCompany?.id) {
      toast.error('Company ID is missing')
      return
    }
    if (!currentCompany?.phone?.trim()) {
      toast.error(
        'Company phone is required for outbound calls. Please configure a phone number in company settings.',
      )
      return
    }

    setIsSubmitting(true)
    try {
      if (onSubmitProp) {
        await onSubmitProp(data)
      } else {
        let e164Phone = data.phone
        try {
          const phoneNumber = parsePhoneNumberWithError(data.phone, 'US')
          e164Phone = phoneNumber.format('E.164')
        } catch {
          // use as-is
        }

        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:6060'
        const roomName = `lease-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`

        // property is the listing address string — matches what the lead creation flow sends
        const listingAddress = selectedListing?.data?.address

        const prepareRes = await fetch(
          `${apiUrl}/api/voice/conferences/outbound/prepare`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              to: e164Phone,
              companyId: currentCompany.id,
              callId: roomName,
              firstName: data.firstName,
              lastName: data.lastName,
            }),
          },
        )
        if (!prepareRes.ok) {
          throw new Error(
            `Failed to prepare outbound session: ${prepareRes.statusText}`,
          )
        }
        const {
          data: { sessionId },
        } = await prepareRes.json()

        const metadata: LeaseAgentMetadata = {
          callType: OutboundCallType.LEASING,
          phoneTree: [e164Phone],
          transfer_to: null,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          property: listingAddress ?? null,
          message: data.message,
          instructions: data.instructions,
          companyId: currentCompany.id,
          from: currentCompany.phone,
          sessionId,
          variableValues: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            companyName: currentCompany.name,
            agentName: currentCompany.name,
            ...(listingAddress && { property: listingAddress }),
          },
        }

        const response = await fetch(`${apiUrl}/api/livekit/dispatch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            agentName: 'outbound-caller',
            roomName,
            metadata,
            envPrefix: 'LEASING_OUTBOUND',
          }),
        })

        if (!response.ok) {
          const errorText = await response.text()
          console.error('Agent dispatch error:', errorText)
          throw new Error(`Failed to dispatch agent: ${response.statusText}`)
        }

        toast.success('Lease inquiry agent dispatched successfully')
      }

      onOpenChange(false)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to dispatch lease agent',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Simulate Lease Inquiry</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid grid-cols-2 gap-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="john.doe@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="+49 152 2390187 or (555) 123-4567"
                      {...field}
                      onChange={e => {
                        const formatted = formatPhoneNumber(e.target.value)
                        field.onChange(formatted)
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>Listing (Optional)</FormLabel>
              <FormControl>
                <SearchDropdown
                  fetchItems={fetchListings}
                  selectedItems={selectedListing}
                  onSelect={item =>
                    setSelectedListing(
                      item as SearchDropdownItem<Listing> | null,
                    )
                  }
                  placeholder="Select a listing"
                  searchPlaceholder="Search listings..."
                  multiSelect={false}
                  loadInitialItems={true}
                />
              </FormControl>
            </FormItem>

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="I'm interested in your listing..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Accordion
              type="single"
              collapsible
              value={accordionValue}
              onValueChange={setAccordionValue}
            >
              <AccordionItem value="instructions" className="border-none">
                <div className="flex items-center gap-2">
                  <AccordionTrigger className="flex-1 py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Instructions</span>
                      {storedInstructions && (
                        <span className="text-xs text-muted-foreground">
                          • Saved
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  {storedInstructions && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleClearInstructions}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <AccordionContent className="px-0 pb-0 pt-0">
                  <FormField
                    control={form.control}
                    name="instructions"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea
                            placeholder="Add special instructions for this call..."
                            rows={4}
                            {...field}
                            onBlur={e => {
                              field.onBlur()
                              handleInstructionsChange(e.target.value)
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Simulating...' : 'Simulate'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
